/**
 * @fileoverview The Export dropdown on a Students section: one button, a short
 * menu of downloads (the roster as CSV, attendance as a workbook).
 *
 * Each item is a GET on the portal's own api client with a blob response, the
 * same round-trip the program and roster exports already use: the download
 * stays behind the client's auth (a 401 still sends the reader to sign-in) and
 * a failure is a toast instead of an error body in a new tab.
 *
 * Hand-rolled like AccountMenu: a Radix menu is not installed and this is two
 * commands. Outside-click and Escape close it; the trigger reports
 * `aria-expanded`.
 */

import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

import { Button, Icon } from "@/components/studio/primitives";

/** A filename-safe slug, the same shape the server's download names use. */
export function exportSlug(text, fallback) {
  return (
    (text || fallback)
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "")
      .slice(0, 60) || fallback
  );
}

/** Save a blob response under a name, without leaving the page. */
export function saveBlob(data, filename, type) {
  const url = URL.createObjectURL(new Blob([data], { type }));
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

export const CSV_TYPE = "text/csv";
export const XLSX_TYPE = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

/**
 * A class's two downloads, for either portal: `base` is the class's path on
 * that portal's api (`/org/classes/:id` or `/classes/:id`), both of which
 * answer `roster/export` and `attendance/export`.
 */
export function classExportItems(base, className) {
  const slug = exportSlug(className, "class");
  return [
    {
      id: "roster",
      label: "Roster",
      hint: "CSV · names, consent and notes",
      url: `${base}/roster/export`,
      filename: `hans-${slug}-roster.csv`,
      type: CSV_TYPE,
    },
    {
      id: "attendance",
      label: "Attendance",
      hint: "Spreadsheet · every day this class met",
      url: `${base}/attendance/export`,
      filename: `hans-${slug}-attendance.xlsx`,
      type: XLSX_TYPE,
    },
  ];
}

/** Fetch one export as a blob and save it; a failure is a toast, never a throw. */
async function download(client, item, errorMessage) {
  try {
    const resp = await client.get(item.url, { responseType: "blob" });
    saveBlob(resp.data, item.filename, item.type);
  } catch (err) {
    toast.error(errorMessage(err, `Couldn't download the ${(item.what || item.label).toLowerCase()}`));
  }
}

/**
 * One download behind one button: the org Dashboard's Export, which always
 * exports the tab being read, so there is nothing to choose from a menu.
 *
 * @param {object} props
 * @param {import("axios").AxiosInstance} props.client
 * @param {{url: string, filename: string, type: string, label: string, what?: string}} props.item
 * @param {(err: unknown, fallback: string) => string} props.errorMessage
 * @param {string} [props.testId]
 */
export function ExportButton({ client, item, errorMessage, testId = "export-button" }) {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      await download(client, item, errorMessage);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Button variant="ghost" size="sm" icon="download" onPress={run} disabled={busy} data-testid={testId}>
      {busy ? "Preparing…" : item.label}
    </Button>
  );
}

/**
 * @param {object} props
 * @param {import("axios").AxiosInstance} props.client The portal's api client (api or orgApi).
 * @param {{id: string, label: string, hint?: string, url: string, filename: string, type: string}[]} props.items
 * @param {(err: unknown, fallback: string) => string} props.errorMessage The portal's error formatter.
 * @param {string} [props.testId]
 */
export default function ExportMenu({ client, items, errorMessage, testId = "export-menu", label = "Export" }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(null);
  const root = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (root.current && !root.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const run = async (item) => {
    setOpen(false);
    setBusy(item.id);
    try {
      await download(client, item, errorMessage);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div ref={root} className="relative inline-block">
      <Button
        variant="ghost"
        size="sm"
        icon="download"
        iconRight="chevronDown"
        onPress={() => setOpen((v) => !v)}
        disabled={!!busy}
        aria-haspopup="menu"
        aria-expanded={open}
        data-testid={testId}
      >
        {busy ? "Preparing…" : label}
      </Button>
      {open && (
        <div
          role="menu"
          aria-label={label}
          data-testid={`${testId}-panel`}
          className="absolute right-0 top-full z-30 mt-2 w-[264px] rounded-2xl border border-[var(--studio-divider-strong)] bg-[var(--studio-glass)] p-1.5 shadow-[var(--studio-shadow)] backdrop-blur-xl backdrop-saturate-150"
        >
          {items.map((item) => (
            <button
              key={item.id}
              type="button"
              role="menuitem"
              onClick={() => run(item)}
              data-testid={`${testId}-${item.id}`}
              className="flex w-full items-start gap-2.5 rounded-xl px-3 py-2.5 text-left transition-colors hover:bg-[var(--studio-overlay)]"
            >
              <Icon name="download" size={15} className="mt-0.5 shrink-0 text-[var(--text-tertiary)]" />
              <span>
                <span className="block font-sans text-[14px] text-[var(--text-primary)]">{item.label}</span>
                {item.hint && <span className="block font-sans text-[12px] text-[var(--text-tertiary)]">{item.hint}</span>}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
